/**
 * One transition coordinator — Module C.
 * Queue the latest, drop the rest. A running transition always finishes;
 * the site then goes to the most recent navigation request.
 */

import { IS_OWNER } from './ownerMode'

export const TRANSITION_KINDS = {
  intro: 'intro',
  slide: 'slide',
  reveal: 'reveal',
  theater: 'theater',
  dive: 'dive',
}

const createInitialState = () => ({
  active: null,
  activeKinds: [],
  isHubRoute: true,
  queued: null,
})

let state = createInitialState()
const kinds = new Set()
const listeners = new Set()
const kindStartedAt = new Map()
const kindStuckWarned = new Set()

let navigateFn = null
let theaterCloseFn = null

const emit = () => {
  listeners.forEach((listener) => listener(state))
}

const syncActive = () => {
  const activeKinds = [...kinds]
  const active = activeKinds.length ? activeKinds[activeKinds.length - 1] : null
  state = {
    ...state,
    active,
    activeKinds,
  }
  emit()
}

const flushQueuedNavigation = () => {
  if (kinds.size > 0) return
  const next = state.queued
  if (!next) return
  state = { ...state, queued: null }
  emit()
  navigateFn?.(next.path, next.options)
}

export const subscribeTransitionCoordinator = (listener) => {
  listeners.add(listener)
  listener(state)
  return () => listeners.delete(listener)
}

export const getTransitionSnapshot = () => state

export const isBusy = () => kinds.size > 0

export const hasTransitionKind = (kind) => kinds.has(kind)

export const begin = (kind) => {
  if (!kind) return
  kinds.add(kind)
  if (!kindStartedAt.has(kind)) kindStartedAt.set(kind, Date.now())
  kindStuckWarned.delete(kind)
  syncActive()
}

export const end = (kind) => {
  if (!kind || !kinds.has(kind)) return
  kinds.delete(kind)
  kindStartedAt.delete(kind)
  kindStuckWarned.delete(kind)
  syncActive()
  flushQueuedNavigation()
}

export const setRoute = ({ isHubRoute }) => {
  if (state.isHubRoute === Boolean(isHubRoute)) return
  state = { ...state, isHubRoute: Boolean(isHubRoute) }
  emit()
}

export const registerNavigate = (fn) => {
  navigateFn = typeof fn === 'function' ? fn : null
}

export const registerTheaterClose = (fn) => {
  theaterCloseFn = typeof fn === 'function' ? fn : null
}

/**
 * @param {string} path
 * @param {{ explicit?: boolean, immediate?: boolean } & Record<string, unknown>} [options]
 */
export const requestNavigation = (path, options = {}) => {
  if (path == null || path === '') return

  const { explicit = false, immediate = false, ...navOptions } = options
  const theaterActive = kinds.has(TRANSITION_KINDS.theater)

  if (theaterActive && !explicit) return

  if (theaterActive && explicit) {
    theaterCloseFn?.()
  }

  // Dive (and similar) own their navigation at take-off — do not park behind active kinds.
  if (immediate) {
    navigateFn?.(path, navOptions)
    return
  }

  if (kinds.size > 0) {
    state = {
      ...state,
      queued: { path, options: navOptions },
    }
    emit()
    return
  }

  navigateFn?.(path, navOptions)
}

if (typeof window !== 'undefined' && IS_OWNER) {
  window.setInterval(() => {
    const now = Date.now()
    kindStartedAt.forEach((startedAt, kind) => {
      if (now - startedAt < 15000) return
      if (kindStuckWarned.has(kind)) return
      kindStuckWarned.add(kind)
      console.warn(`[transition] kind "${kind}" has been active for more than 15s`)
    })
  }, 5000)
}
